import { useReducer, useState } from "react";
import { useMutation, useQuery } from "react-query";
import apiService from "services/api";
import Select from "react-select";
import { addCourseConfidenceModalReducer, addCourseConfidenceModalReducerActions, initialAddCourseConfidenceModalReducer } from "./reducer";
import { Controller } from "react-hook-form";
import { useMemo } from "react";
import { FormEntry, NakedTable, CustomAlert } from "components";
import EditCourseConfidenceModal from "./EditCourseConfidenceModal";
import { Button } from "reactstrap";
import { FiSearch, FiTrash } from "react-icons/fi";
import { useAddCourseConfidenceForm, useDeleteCourseConfidence, useMutateCourseConfidence } from "./form";
import { useCourseConfidenceQuery } from "./queries";

const CourseConfidencePage = () => {
	const { data } = useQuery(["course", "all"], () => apiService.course.get());
	const {
		control,
		register,
		handleSubmit,
		formState: { errors },
		watch,
	} = useAddCourseConfidenceForm();

	const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
	const [courseConfidenceRecordToDelete, setCourseConfidenceRecordToDelete] = useState(false);

	const { data: courseConfidenceRecords } = useCourseConfidenceQuery();

	const courseConfidenceMap = useMemo(() => {
		if (courseConfidenceRecords === undefined) return {};
		var map = {};
		for (var i = 0; i < courseConfidenceRecords.length; i++) {
			map[courseConfidenceRecords[i].lessonId] = true;
		}
		return map;
	}, [courseConfidenceRecords]);

	const selectedCourseId = watch("courseId");

	const courseOptions = useMemo(() => data?.map((course) => ({ label: course.name, value: course.id })) || [], [data]);

	const lessonOptions = useMemo(() => {
		return selectedCourseId === undefined || data === undefined
			? []
			: data
					?.find((course) => course.id === selectedCourseId)
					.lessons.filter((lesson) => {
						return !lesson.isCustomLesson && !courseConfidenceMap[lesson.id];
					})
					.map((lesson) => ({ label: lesson.name, value: lesson.id }));
	}, [selectedCourseId]);

	const { mutate: addCourseConfidence, isLoading } = useMutateCourseConfidence();

	const [courseConfidenceModalState, courseConfidenceModalDispatch] = useReducer(
		addCourseConfidenceModalReducer,
		initialAddCourseConfidenceModalReducer
	);

	const { mutate: deleteCourseConfidence } = useDeleteCourseConfidence(courseConfidenceRecordToDelete?.id, () => setDeleteConfirmationModal(false));

	return (
		<div className="content">
			<h5 className="font-weight-bold text-center">Add Course Confidence</h5>

			<form onSubmit={handleSubmit((data) => addCourseConfidence(data))} className="mt-4">
				<div className="mb-3">
					<Controller
						control={control}
						name="courseId"
						render={({ field: { value, onChange } }) => (
							<Select
								className="react-select info w-100"
								classNamePrefix="react-select"
								name="singleSelect"
								value={courseOptions.find((course) => course.value === value)}
								onChange={(selectedCourse) => {
									onChange(selectedCourse.value);
								}}
								options={courseOptions}
								placeholder="Select Course"
							/>
						)}
					/>
				</div>
				<div className="mb-3">
					<Controller
						control={control}
						name="lessonId"
						render={({ field: { value, onChange } }) => (
							<Select
								className="react-select info w-100"
								classNamePrefix="react-select"
								name="singleSelect"
								value={lessonOptions.find((course) => course.value === value)}
								onChange={(selectedLesson) => {
									onChange(selectedLesson.value);
								}}
								options={lessonOptions}
								placeholder="Select Lesson"
								isDisabled={!lessonOptions?.length}
							/>
						)}
					/>
				</div>
				<div className="mt-3">
					<FormEntry
						register={register}
						name="confidenceLevel"
						type="number"
						formLabel="Confidence Level"
						step={10}
						required
						error={errors.confidenceLevel}
					/>
				</div>

				<div className="d-flex justify-content-end">
					<Button type="submit" color="success">
						Submit
					</Button>
				</div>
			</form>

			<EditCourseConfidenceModal
				courseConfidenceModalState={courseConfidenceModalState}
				courseConfidenceModalDispatch={courseConfidenceModalDispatch}
			/>

			<CustomAlert
				title="Remove Record"
				show={deleteConfirmationModal}
				hideModal={() => setDeleteConfirmationModal(false)}
				description={
					<>
						Are you sure you want to remove entry{" "}
						<span className="text-danger font-weight-bold">{courseConfidenceRecordToDelete?.course?.name}?</span>
					</>
				}
				disabled={isLoading}
				submitModal={deleteCourseConfidence}
				submitButtonText="Delete"
			/>

			<NakedTable
				data={courseConfidenceRecords}
				columns={[
					{
						id: "courseName",
						header: "Course",
						accessor: (row) => row.course.name,
						sortable: true,
						filterable: true,
					},
					{
						id: "lessonName",
						header: "Lesson",
						accessor: (row) => row.lesson.name,
						sortable: true,
						filterable: true,
					},
					{
						id: "confidenceLevel",
						header: "Confidence Level",
						accessor: (row) => row.confidenceLevel,
						sortable: true,
						filterable: true,
					},
					{
						id: "Action",
						header: "View",
						alignment: "center",
						accessor: (row) => (
							<div className="d-flex justify-content-center">
								<FiSearch
									size={20}
									className="text-warning mr-2"
									onClick={() => courseConfidenceModalDispatch({ type: addCourseConfidenceModalReducerActions.OPEN, payload: row })}
									style={{ cursor: "pointer" }}
								/>
								<FiTrash
									size={20}
									className="text-danger mr-2"
									onClick={() => {
										setDeleteConfirmationModal(true);
										setCourseConfidenceRecordToDelete(row);
									}}
									style={{ cursor: "pointer" }}
								/>
							</div>
						),
						sortable: false,
						filterable: false,
					},
				]}
				title="Course Confidence Entries"
				className="mt-3"
			/>
		</div>
	);
};

export default CourseConfidencePage;
